import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { FormInstance } from 'antd';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import { signUp } from 'aws-amplify/auth';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MSG_PASSWORD_REQUIREMENT, REGEX_PASSWORD } from '../../../constants';
import giggLogo from '../../../images/gigg-logo.svg';
import variables from '../../../styles/variables.module.scss';
import { setPageTitle } from '../../../utils/Utils';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';

type FieldType = {
  givenName: string;
  familyName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

type ErrorType = {
  name: string;
  message: string;
};

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<ErrorType | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPageTitle('Register');
  }, []);

  const handleFinish = async ({ givenName, familyName, email, password }: FieldType) => {
    try {
      setLoading(true);
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: givenName,
            family_name: familyName,
          },
        },
      });
      message.success('Verification email sent, please verify your email.');
      navigate('/login');
    } catch (err) {
      setError(err as ErrorType);
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (rule: any, value: string, callback: Function, form: FormInstance) => {
    if (value && !form.isFieldValidating('confirmPassword')) {
      form.validateFields(['confirmPassword'], { dirty: true });
    }
    callback();
  };

  const validateConfirmPassword = (rule: any, value: string, callback: Function, form: FormInstance) => {
    const regex = new RegExp(REGEX_PASSWORD);
    if (value && !value.match(regex)) {
      const message = MSG_PASSWORD_REQUIREMENT;
      callback(message);
    }

    if (value && value !== form.getFieldValue('password')) {
      callback('Passwords must match.');
    }

    callback(undefined);
  };

  return (
    <div className="flex-container justify-center router-wrapper ">
      <div className="login-outer">
        <div className="flex-container justify-center">
          <div className="margin-bottom-md">
            <div className="login-image-container">
              <img className="login-img" src={giggLogo} height="auto" width="100%" alt="Gigg" />
            </div>
            <div className="flex-container justify-center">Welcome! Create an account to get started.</div>
          </div>
        </div>
        <div className="login-inner">
          <Form layout="vertical" className="login-form" onFinish={handleFinish}>
            <>
              {error && (
                <Form.Item>
                  <div style={{ color: variables.errorColor }} className="text-center">
                    {error.message}
                  </div>
                </Form.Item>
              )}
              <div className="flex-container flex-gap">
                <div className="flex-grow-1">
                  <Form.Item<FieldType>
                    name="givenName"
                    rules={[{ required: true, message: 'Please input your first name' }]}
                  >
                    <Input className="login-input" placeholder="First Name" />
                  </Form.Item>
                </div>
                <div className="flex-grow-1">
                  <Form.Item<FieldType>
                    name="familyName"
                    rules={[{ required: true, message: 'Please input your Last Name' }]}
                  >
                    <Input className="login-input" placeholder="Last Name" />
                  </Form.Item>
                </div>
              </div>
              <Form.Item<FieldType>
                name="email"
                rules={[
                  { required: true, message: 'Please input your email' },
                  { type: 'email', message: 'Please enter a valid email' },
                ]}
              >
                <Input className="login-input" placeholder="Email" />
              </Form.Item>
              <Form.Item<FieldType>
                name="password"
                rules={[
                  { required: true, message: 'Please input your current password' },
                  { pattern: new RegExp(REGEX_PASSWORD), message: MSG_PASSWORD_REQUIREMENT },
                  (formInstance: FormInstance) => ({
                    validator: (rule, value, callback) => validatePassword(rule, value, callback, formInstance),
                  }),
                ]}
              >
                <Input className="login-input" type="password" placeholder="Password" />
              </Form.Item>
              <Form.Item<FieldType>
                name="confirmPassword"
                rules={[
                  { required: true, message: 'Please confirm your password' },
                  (formInstance: FormInstance) => ({
                    validator: (rule, value, callback) => validateConfirmPassword(rule, value, callback, formInstance),
                  }),
                ]}
              >
                <Input className="login-input" type="password" placeholder="Confirm Password" />
              </Form.Item>
            </>
            {error ? <p style={{ textAlign: 'center', color: '#ff4d4f' }}>{error.message}</p> : null}
            <div className="flex-container margin-bottom-md">
              <div className="flex-container flex">
                <Button className="login-button" type="primary" htmlType="submit">
                  {loading ? (
                    <FontAwesomeIcon icon={faSpinnerThird} size="lg" className="fa-fast-spin" />
                  ) : (
                    <span>Sign Up</span>
                  )}
                </Button>
              </div>
            </div>
            <div className="flex-container">
              <Link to="/login" className="login-link">
                Back to Sign In
              </Link>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
